import React, { useState, useEffect } from 'react';
import { format, subDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNotification } from '../context/NotificationContext';
import './IngresoExpress.css'; 

// === CONFIGURACIÓN UNIVERSAL DE LA API (PRODUCTION READY) ===
const getApiUrl = () => {
  // Check for Vite env var
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  // Check for React Create App env var
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  
  // Default to relative path for production (same domain)
  return ''; 
};

const API_URL = getApiUrl();

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function IngresoExpress() {
  const { showNotification } = useNotification();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  // New state for hotel historical averages
  const [hotelStats, setHotelStats] = useState({}); 

  // Fecha: Ayer
  const yesterday = format(subDays(new Date(), 1), 'yyyy-MM-dd');
  const yesterdayLabel = format(subDays(new Date(), 1), 'd MMM yyyy', { locale: es });

  // 1. Fetch Historical Averages (Avg + Std Dev)
  useEffect(() => {
    const fetchStats = async () => {
        try {
            const response = await fetch(`${API_URL}/api.php?path=hotels/averages`, { headers: getAuthHeader() });
            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setHotelStats(data.data);
                }
            }
        } catch (err) {
            console.error("Failed to load hotel averages", err);
        }
    };
    fetchStats();
  }, []);

  // 2. Load Hotels & Daily Metrics
  useEffect(() => {
    const loadIngresoExpress = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const compsetId = user.default_compset_id;

        if (!compsetId) {
          showNotification('No tienes un compset asignado', 'error');
          setLoading(false);
          return;
        }

        // A. Obtener hoteles del compset
        const compsetRes = await fetch(
          `${API_URL}/api.php?path=compsets/${compsetId}/hotels`,
          { headers: getAuthHeader() }
        );
        const compsetData = await compsetRes.json();
        if (!compsetData.success || !Array.isArray(compsetData.data)) {
          showNotification('Error cargando compset', 'error');
          setLoading(false);
          return;
        }
        const hotelIds = compsetData.data.map(id => parseInt(id));

        // B. Obtener todos los hoteles
        const hotelsRes = await fetch(
          `${API_URL}/api.php?path=hotels&limit=999`,
          { headers: getAuthHeader() }
        );
        const hotelsData = await hotelsRes.json();
        if (!hotelsData.success || !hotelsData.data?.hotels) {
          showNotification('Error cargando hoteles', 'error');
          setLoading(false);
          return;
        }

        // C. Cargar métricas de ayer
        const metricsPromises = hotelIds.map(hotelId =>
          fetch(
            `${API_URL}/api.php?path=daily-metrics&hotelId=${hotelId}&startDate=${yesterday}&endDate=${yesterday}`,
            { headers: getAuthHeader() }
          )
            .then(r => r.ok ? r.json() : { success: false, data: [] })
            .catch(() => ({ success: false, data: [] }))
        );

        const metricsResults = await Promise.all(metricsPromises);

        // D. Construir lista final
        const finalHotels = hotelIds.map((hotelId, index) => {
          const hotelInfo = hotelsData.data.hotels.find(h => h.hotel_id === hotelId);
          const metricResult = metricsResults[index];
          
          const savedData = (metricResult.success && metricResult.data && metricResult.data.length > 0)
            ? metricResult.data[0]
            : {};

          return {
            hotel_id: hotelId,
            name: hotelInfo?.name || 'Hotel sin nombre',
            type: hotelInfo?.type === 'Propio' ? 'Propio' : 'Competencia',
            // Max capacity for checks
            total_rooms: hotelInfo?.total_rooms_available ? parseInt(hotelInfo.total_rooms_available, 10) : 0,
            occupied_rooms: savedData.occupied_rooms?.toString() || '',
            average_rate: savedData.average_rate ? Number(savedData.average_rate).toFixed(2) : '',
            saved: !!savedData.occupied_rooms // Initial saved state
          };
        });

        // Orden: Propio arriba, luego alfabético
        finalHotels.sort((a, b) => {
          if (a.type === 'Propio' && b.type !== 'Propio') return -1;
          if (a.type !== 'Propio' && b.type === 'Propio') return 1;
          return a.name.localeCompare(b.name);
        });

        setHotels(finalHotels);
      } catch (err) {
        console.error('Error en Ingreso Express:', err);
        showNotification('Error cargando datos', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadIngresoExpress();
  }, [showNotification]);

  const saveHotel = async (hotel) => {
    if (!hotel.occupied_rooms || !hotel.average_rate) {
      showNotification(`Completa ambos campos para ${hotel.name}`, 'warning');
      return;
    }

    const occupied = parseInt(hotel.occupied_rooms, 10);
    const rate = parseFloat(hotel.average_rate);

    // --- CAPACITY VALIDATION (Blocking) ---
    if (hotel.total_rooms > 0 && occupied > hotel.total_rooms) {
      showNotification(
        `Error: ${hotel.name} solo tiene ${hotel.total_rooms} habitaciones. No puedes ingresar ${occupied}.`, 
        'error'
      );
      return; // Stop execution
    }

    // --- RATE VALIDATION (Non-Blocking Warning) ---
    const stats = hotelStats[hotel.hotel_id];
    if (stats && stats.avg > 0) {
        const highLimit = stats.avg * 1.9; 
        const lowLimit = stats.avg * 0.1; 
        
        if (rate > highLimit || (rate < lowLimit && rate > 0)) {
            showNotification('Advertencia: La tarifa ingresada es inusual (muy alta o muy baja). Se guardará de todas formas.', 'warning');
        }
    }

    const payload = {
      hotel_id: hotel.hotel_id,
      date: yesterday,
      occupied_rooms: occupied,
      average_rate: rate
    };

    try {
      const response = await fetch(`${API_URL}/api.php?path=daily-metrics-save`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        showNotification(`${hotel.name} → Guardado`, 'success');
        
        setHotels(prev => prev.map(h =>
          h.hotel_id === hotel.hotel_id
            ? { 
                ...h, 
                occupied_rooms: occupied.toString(), 
                average_rate: rate.toFixed(2),
                saved: true 
              }
            : h
        ));
      } else {
        showNotification(`${hotel.name}: ${result.error?.message || 'Error'}`, 'error');
      }
    } catch (err) {
      showNotification('Error de conexión', 'error');
    }
  };

  if (loading) return <div className="express-loading">Cargando datos...</div>;
  if (hotels.length === 0) return <div className="express-container"><div className="express-card">Tu compset está vacío.</div></div>;

  return (
    <div className="express-container">
      <div className="express-header">
        Ingreso de información - {yesterdayLabel}
        <small></small>
      </div>

      <div className="express-card">
        <table className="express-table">
          <thead>
            <tr>
              <th style={{ width: '30%' }}>HOTEL</th>
              <th style={{ width: '25%' }}>CUARTOS OCUPADOS</th>
              <th style={{ width: '25%' }}>TARIFA PROMEDIO</th>
              <th style={{ width: '20%', textAlign: 'right' }}></th>
            </tr>
          </thead>
          <tbody>
            {hotels.map(hotel => {
                // --- Visual Validations ---
                const isCapacityExceeded = hotel.total_rooms > 0 && parseInt(hotel.occupied_rooms) > hotel.total_rooms;

                // Rate Anomaly Check
                const enteredRate = parseFloat(hotel.average_rate);
                const stats = hotelStats[hotel.hotel_id];
                let isRateAnomaly = false;
                let avgRate = 0;

                if (stats && stats.avg > 0 && !isNaN(enteredRate) && enteredRate > 0) {
                    avgRate = stats.avg;
                    const highLimit = avgRate * 1.9;
                    const lowLimit = avgRate * 0.1;
                    if (enteredRate > highLimit || enteredRate < lowLimit) {
                        isRateAnomaly = true;
                    }
                }

                return (
                  <tr 
                    key={hotel.hotel_id} 
                    className={hotel.type === 'Propio' ? 'own-hotel' : 'comp-hotel'}
                  >
                    <td className="hotel-name">
                      <strong>{hotel.name}</strong>
                      <small>{hotel.type}</small>
                      {hotel.total_rooms > 0 && (
                        <div style={{fontSize: '10px', color: '#999', marginTop: '2px'}}>
                          Max: {hotel.total_rooms} habs
                        </div>
                      )}
                    </td>
                    <td>
                      <input
                        type="number"
                        value={hotel.occupied_rooms}
                        onChange={e => setHotels(prev => prev.map(h =>
                          h.hotel_id === hotel.hotel_id ? { ...h, occupied_rooms: e.target.value, saved: false } : h
                        ))}
                        placeholder="0"
                        style={{
                          borderColor: isCapacityExceeded ? 'red' : '',
                          color: isCapacityExceeded ? 'red' : '',
                          borderWidth: isCapacityExceeded ? '2px' : '1px'
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        value={hotel.average_rate}
                        onChange={e => setHotels(prev => prev.map(h =>
                          h.hotel_id === hotel.hotel_id ? { ...h, average_rate: e.target.value, saved: false } : h
                        ))}
                        placeholder="0.00"
                        style={{ 
                            borderColor: isRateAnomaly ? '#ff9800' : '', // Orange for warning
                            color: isRateAnomaly ? '#e65100' : '',
                            borderWidth: isRateAnomaly ? '2px' : '1px'
                        }}
                      />
                      {/* Visible Warning Text */}
                      {isRateAnomaly && (
                        <div style={{ color: '#e65100', fontSize: '0.7rem', marginTop: '2px', lineHeight: '1.1', fontWeight: 'bold' }}>
                            Inusual (Prom: ${avgRate.toFixed(0)})
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className="save-btn"
                        style={{ 
                          backgroundColor: hotel.saved ? '#4CAF50' : '',
                          borderColor: hotel.saved ? '#4CAF50' : '',
                          color: hotel.saved ? 'white' : ''
                        }}
                        onClick={() => saveHotel(hotel)}
                        disabled={!hotel.occupied_rooms || !hotel.average_rate}
                      >
                        {hotel.saved ? 'Guardado' : 'Guardar'}
                      </button>
                    </td>
                  </tr>
                );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default IngresoExpress;